/**
 * floatingVueThemes — THE APP'S floating-vue THEMES, AND WHICH OF THEM ARE OBSTACLES.
 *
 * Two facts about the same object, so they can never disagree: the theme
 * declarations the plugin is installed with, and the set of theme CLASSES a
 * body popper must carry before the overlay registry will treat it as something
 * other overlays move out of the way of (design D4).
 *
 * WHY THEY LIVE TOGETHER. The bridge's rule is "dropdowns and menus are
 * obstacles; hover tooltips are not" — a `v-tooltip` that shoved an open panel
 * aside would be absurd. Which app themes count as menus is not a property of
 * the observer: it is a property of THIS object, because an app theme is a menu
 * exactly when its `$extend` chain reaches floating-vue's `menu` or `dropdown`.
 * Add a fifth theme below and `obstacleThemeClasses()` answers for it on the
 * next call, with no second list to remember. A hand-written roster of class
 * names is the drift this module exists to prevent.
 *
 * THE THEMES ARE NOT FROZEN, deliberately. `app.use(FloatingVue, { themes })`
 * hands this object to the plugin's own recursive config merge, which ASSIGNS
 * each theme object into floating-vue's config tree rather than copying it. A
 * frozen nested theme would make any later merge pass throw in module scope,
 * which is a behaviour change at a vendor seam for no gain — this module is the
 * only writer, and it never writes.
 *
 * @see openspec/changes/overlay-primitive/design.md — D4
 * @see ./floatingVueObserver.js — the one reader of `obstacleThemeClasses()`
 */

/**
 * The themes `entrypoints/application.js` installs floating-vue with. Moved out
 * of the entrypoint verbatim: a boot script vitest cannot mount is the wrong
 * home for a declaration two other modules need to read.
 */
export const FLOATING_VUE_THEMES = {
  instant: {
    $extend: "menu",
    delay: {
      show: 0,
      hide: 0,
    },
  },
  "quick-menu": {
    $extend: "menu",
    delay: {
      show: 300,
      hide: 100,
    },
  },
  "stick-menu": {
    $extend: "menu",
    autoHide: false,
    delay: {
      show: 300,
    },
  },
  "diagram-popup": {
    $extend: "menu",
    autoHide: false,
    delay: {
      show: 1000,
    },
  },
  // The expertise ring's text-only tooltip. It extends `tooltip`, so it is
  // never an obstacle; `floatingVueBridge.css` lets the pointer pass through it
  // and breaks its lines, so it can sit over an unpinned glance card without
  // dismissing it.
  expertise: {
    $extend: "tooltip",
  },
};

/**
 * floating-vue's own two clickable themes, and the only names in this module
 * that are written by hand — they belong to the vendor, so there is nothing to
 * derive them from. `menu` extends `dropdown`; `tooltip` extends neither, which
 * is what keeps the ~85 `v-tooltip` sites out of the obstacle set.
 */
const VENDOR_OBSTACLE_THEMES = ["dropdown", "menu"];

/** The class floating-vue stamps on a popper for one theme name. */
function themeClass(name) {
  return `v-popper--theme-${name}`;
}

/**
 * Every theme class that makes a shown popper an obstacle: the two vendor
 * themes plus every app theme whose `$extend` chain reaches one of them.
 *
 * Computed as a fixed point rather than a single pass, so a theme that extends
 * another APP theme is included too, and a `$extend` cycle simply never joins
 * the set instead of hanging.
 *
 * WHY THE WHOLE CHAIN IS SPELT OUT, when floating-vue usually stamps it for us.
 * `getThemeClasses` puts the theme AND its ancestors on the node — an `instant`
 * popper normally carries `v-popper--theme-menu` and `-dropdown` as well — but
 * it stops walking at a theme declaring `$resetCss`, which would leave a menu
 * wearing only its own name. Matching on the derived set is correct either way.
 *
 * @returns {string[]} class names, vendor roots first, then the app themes.
 */
export function obstacleThemeClasses() {
  const names = new Set(VENDOR_OBSTACLE_THEMES);

  let grew = true;
  while (grew) {
    grew = false;
    for (const [name, spec] of Object.entries(FLOATING_VUE_THEMES)) {
      if (names.has(name)) continue;
      if (!names.has(spec?.$extend)) continue;
      names.add(name);
      grew = true;
    }
  }

  return [...names].map(themeClass);
}
